import {
  quantizeMicrophoneLevel,
  smoothMicrophoneLevel,
  toMicrophoneLevel,
} from './shared-responder-v2-microphone-level.helpers';

/**
 * The level is sampled on a timer rather than on animation frames: frames are
 * throttled to a standstill in a background tab, which would leave the last
 * loud reading standing while the microphone is still open.
 */
export const MICROPHONE_LEVEL_SAMPLE_INTERVAL_MS = 100;

export interface MicrophoneAudioSample {
  readonly audioLevel: number;
  readonly rawLevel: number;
  readonly sampledAt: number;
  readonly isRunning: boolean;
}

/** ~11ms of audio per frame at 44.1kHz — one syllable spans several of them. */
const MICROPHONE_LEVEL_SAMPLE_SIZE = 512;

export interface MicrophoneLevelMeter {
  readonly start: (
    stream: MediaStream,
    onSample?: (sample: MicrophoneAudioSample) => void
  ) => void;
  readonly stop: () => void;
}

/**
 * Publishes the normalised loudness of an already-open capture stream, and only
 * when the quantised value moves — a caller that re-renders on every sample
 * re-renders ten times a second for no visible difference.
 *
 * The stream stays the caller's to release: this owns the audio graph it built
 * on top of it and nothing else.
 */
export function createMicrophoneLevelMeter(
  onLevel: (level: number) => void,
  onSample?: (sample: MicrophoneAudioSample) => void
): MicrophoneLevelMeter {
  let context: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;
  let samples: Uint8Array<ArrayBuffer> | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let smoothed = 0;
  let published = 0;
  let sampleListener = onSample;

  function publish(level: number): void {
    if (level === published) return;

    published = level;
    onLevel(level);
  }

  function sample(): void {
    if (analyser === null || samples === null) return;

    analyser.getByteTimeDomainData(samples);
    const rawLevel = toMicrophoneLevel(samples);
    smoothed = smoothMicrophoneLevel(smoothed, rawLevel);

    publish(quantizeMicrophoneLevel(smoothed));
    sampleListener?.({
      audioLevel: quantizeMicrophoneLevel(smoothed),
      rawLevel,
      sampledAt: performance.now(),
      isRunning: context?.state === 'running',
    });
  }

  function stop(): void {
    if (timer !== null) clearInterval(timer);

    source?.disconnect();
    analyser?.disconnect();
    void context?.close().catch(() => undefined);

    timer = null;
    source = null;
    analyser = null;
    samples = null;
    context = null;
    smoothed = 0;

    publish(0);
  }

  function start(stream: MediaStream, listener = onSample): void {
    stop();
    sampleListener = listener;

    if (typeof AudioContext === 'undefined') return;

    const nextContext = new AudioContext();
    const nextAnalyser = nextContext.createAnalyser();

    nextAnalyser.fftSize = MICROPHONE_LEVEL_SAMPLE_SIZE;

    const nextSource = nextContext.createMediaStreamSource(stream);

    // The analyser is a leaf: connecting it onwards would play the responder's
    // own microphone back at them.
    nextSource.connect(nextAnalyser);

    // A context built outside a user gesture starts suspended, and a suspended
    // graph never advances the analyser.
    void nextContext.resume().catch(() => undefined);

    context = nextContext;
    analyser = nextAnalyser;
    source = nextSource;
    samples = new Uint8Array(new ArrayBuffer(nextAnalyser.fftSize));
    timer = setInterval(sample, MICROPHONE_LEVEL_SAMPLE_INTERVAL_MS);
  }

  return { start, stop };
}
